import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { parseHoroscopeToSections } from "./parseHoroscopeToSections"; // ต้องมีฟังก์ชันนี้ตามที่คุยกัน

const GOLD = "#FFD86E";
const GOLD_GRAD =
  "linear-gradient(90deg,#FFD86E 0%, #EAAE2C 45%, #CF7B00 100%)";
const CARD_BG = "linear-gradient(180deg,#0E1732 0%, #0B122A 100%)";

export default function ZodiacWeeklyPage({
  zodiac: zodiacProp, // (optional) override zodiac text
  rangeText: rangeTextProp, // (optional) override range text
  birthDate: birthDateProp, // (optional) YYYY-MM-DD
  birthTime: birthTimeProp, // (optional) HH:mm
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // ---------- tabs (เรียง: ทั่วไป/ความรัก/การเงิน/สุขภาพ) ----------
  const tabs = [
    { key: "general", label: "ทั่วไป", icon: "✨" },
    { key: "love", label: "ความรัก", icon: "❤️" },
    { key: "finance", label: "การเงิน", icon: "💰" },
    { key: "health", label: "สุขภาพ", icon: "➕" },
  ];

  // ---------- UI state ----------
  const [tab, setTab] = useState("general");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // ---------- data from API ----------
  const [zodiac, setZodiac] = useState(zodiacProp || "ลัคนาราศี");
  const [rangeText, setRangeText] = useState(rangeTextProp || "");
  const [data, setData] = useState({
    general: { title: "ภาพรวม", body: "", tipTitle: "คำแนะนำวันนี้", tip: "" },
    love: { title: "ความรัก", body: "", tipTitle: "คำแนะนำวันนี้", tip: "" },
    finance: { title: "การเงิน", body: "", tipTitle: "ข้อควรระวัง", tip: "" },
    health: { title: "สุขภาพ", body: "", tipTitle: "ทริคดูแลตัวเอง", tip: "" },
  });
  const [luckyNumbers, setLuckyNumbers] = useState([]); // ⬅️ เลขนำโชค (ถ้ามี)

  // ---------- read birthDate/birthTime from props -> state -> query ----------
  const search = new URLSearchParams(location.search);
  // รองรับทั้งแบบ birthDate/birthTime และ date/time
  const birthDateQS = search.get("birthDate") || search.get("date") || "";
  const birthTimeQS = search.get("birthTime") || search.get("time") || "";

  const stateBirthDate = location.state?.birthDate;
  const stateBirthTime = location.state?.birthTime;

  const birthDate = useMemo(
    () => birthDateProp || stateBirthDate || birthDateQS || "",
    [birthDateProp, stateBirthDate, birthDateQS]
  );
  const birthTime = useMemo(
    () => birthTimeProp || stateBirthTime || birthTimeQS || "",
    [birthTimeProp, stateBirthTime, birthTimeQS]
  );

  // ---------- UI subcomponents ----------
  const Pill = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      type="button"
      className={`inline-flex items-center justify-center
                  h-9 px-3 rounded-full
                  text-[13px] font-semibold
                  whitespace-nowrap transition-transform active:translate-y-[1px]
                  ${active ? "text-[#1B1300]" : "text-white"}`}
      style={{
        background: active ? GOLD_GRAD : "transparent",
        border: `1.25px solid ${GOLD}`,
      }}
    >
      {children}
    </button>
  );

  const SectionCard = ({ children, padded = true }) => (
    <div
      className="rounded-[22px] p-[1.5px] mt-4"
      style={{ borderRadius: 22, background: GOLD_GRAD }}
    >
      <section
        className={`rounded-[20px] ${padded ? "px-5 py-5" : ""}`}
        style={{ background: CARD_BG }}
      >
        {children}
      </section>
    </div>
  );

  const content = data[tab];

  // ✅ เปิดเพจแล้วสกรอลไปบนสุดเสมอ
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual"; // ปิด auto-restore ของเบราว์เซอร์
    }
    // ใช้ rAF ให้ทำหลัง DOM วาดเสร็จ
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
    return () => cancelAnimationFrame(id);
  }, []);
  
  // ---------- fetch API once birthDate & birthTime available ----------
  useEffect(() => {
    if (!birthDate || !birthTime) return;

    const fetchAscendant = async () => {
      setLoading(true);
      setErr("");

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BIGC_BIGLINE_API_URL}/horoscope/get-horoscope-ascendant-sign`,
          { birthDate, birthTime },
          {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_BIGC_BIGLINE_API_KEY,
            },
          }
        );

        const payload = res?.data?.data || {};
        const zodiacText = payload.zodiac || "ไม่ทราบราศี";
        setZodiac(`ลัคนาราศี ${zodiacText}`);

        // ช่วงเวลา (ถ้าไม่ได้ส่งมาทาง props)
        if (!rangeTextProp) {
          const maybeRange =
            payload.rangeText || payload.weekly_range || payload.period || "";
          setRangeText(maybeRange);
        }

        // ---------- ใช้ sections ถ้ามี, ไม่งั้น parse จาก horoscope ----------
        if (payload.sections) {
          const sections = payload.sections;
          const merged = {
            general: {
              title: sections?.general?.title || "ภาพรวม",
              body: sections?.general?.body || payload.horoscope || "",
              tipTitle: sections?.general?.tipTitle || "คำแนะนำวันนี้",
              tip: sections?.general?.tip || payload.advice || "",
            },
            love: {
              title: sections?.love?.title || "ความรัก",
              body: sections?.love?.body || "",
              tipTitle: sections?.love?.tipTitle || "คำแนะนำวันนี้",
              tip: sections?.love?.tip || "",
            },
            finance: {
              title: sections?.finance?.title || "การเงิน",
              body: sections?.finance?.body || "",
              tipTitle: sections?.finance?.tipTitle || "ข้อควรระวัง",
              tip: sections?.finance?.tip || "",
            },
            health: {
              title: sections?.health?.title || "สุขภาพ",
              body: sections?.health?.body || "",
              tipTitle: sections?.health?.tipTitle || "ทริคดูแลตัวเอง",
              tip: sections?.health?.tip || "",
            },
          };
          setData(merged);
          // ถ้า API มีเลขนำโชคแยกมาเอง อาจจะอ่าน payload.luckyNumbers ได้ที่นี่
          if (Array.isArray(payload.luckyNumbers))
            setLuckyNumbers(payload.luckyNumbers);
        } else if (payload.horoscope) {
          // ⬅️ เคสของคุณ: ได้เป็นข้อความยาว — แยกหมวดอัตโนมัติ
          const { sections, luckyNumbers } = parseHoroscopeToSections(
            payload.horoscope
          );
          setData(sections);
          setLuckyNumbers(luckyNumbers || []);
        } else {
          // fallback
          setData({
            general: {
              title: "ภาพรวม",
              body: payload.advice || "",
              tipTitle: "คำแนะนำวันนี้",
              tip: "",
            },
            love: {
              title: "ความรัก",
              body: "",
              tipTitle: "คำแนะนำวันนี้",
              tip: "",
            },
            finance: {
              title: "การเงิน",
              body: "",
              tipTitle: "ข้อควรระวัง",
              tip: "",
            },
            health: {
              title: "สุขภาพ",
              body: "",
              tipTitle: "ทริคดูแลตัวเอง",
              tip: "",
            },
          });
          setLuckyNumbers([]);
        }

        // GTM: view ascendant
        window.dataLayer.push({
          event: "click_view_ascendant",
          category: "Ascendant",
          birth_date: birthDate,
          birth_time: birthTime,
          ascendant: zodiacText || "unknown",
        });
      } catch (e) {
        console.error("Ascendant API error:", e);
        setErr("ไม่สามารถดึงข้อมูลทำนายได้ กรุณาลองใหม่อีกครั้ง");
        setLuckyNumbers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAscendant();
  }, [birthDate, birthTime, rangeTextProp]);

  return (
    <div className="min-h-screen bg-[#0A0F28] text-white relative">
      {/* Back */}
      <header
        className="relative z-10"
        style={{
          position: "absolute",
          paddingTop: "calc(env(safe-area-inset-top,0px) + 10px)",
        }}
      >
        <div className="w-full h-20 px-5 py-2 flex items-end">
          <button
            aria-label="ย้อนกลับ"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center w-9 h-9 rounded-2xl
                       bg-white/10 hover:bg-white/15 active:bg-white/20
                       shadow-[0_4px_14px_rgba(0,0,0,0.35)] backdrop-blur-[2px]
                       focus:outline-none focus:ring-2 focus:ring-white/30"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 19L8 12L15 5"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Body */}
      <main className="px-8 pt-20 pb-12">
        {/* บัตรหัวเรื่องราศี + ช่วงเวลา */}
        <SectionCard padded={false}>
          <div
            className="rounded-[20px] px-6 py-6 text-center"
            style={{
              background: "linear-gradient(180deg,#0E1732 0%, #0B122A 100%)",
              borderRadius: 20,
            }}
          >
            <h1 className="text-[22px] font-extrabold" style={{ color: GOLD }}>
              {zodiac}
            </h1>
            {rangeText ? (
              <>
                <p className="text-white/80 text-[14px] mt-2">ช่วงเวลาทำนาย</p>
                <p className="text-white/90 text-[16px] mt-0.5">{rangeText}</p>
              </>
            ) : null}
            {!birthDate || !birthTime ? (
              <p className="mt-3 text-[13px] text-white/60">
                * ไม่พบวัน–เวลาเกิด (birthDate/birthTime) — ส่งผ่าน props, state
                หรือ query string:
                <span className="ml-1 underline">
                  ?birthDate=YYYY-MM-DD&birthTime=HH:mm
                </span>
              </p>
            ) : null}
          </div>
        </SectionCard>

        {/* Tabs — ปุ่มเล็ก/บรรทัดเดียว + สกรอลล์แนวนอนสำรอง */}
        <div className="mt-5 overflow-x-auto">
          <div className="flex items-center gap-2 whitespace-nowrap">
            {tabs.map((t) => (
              <Pill
                key={t.key}
                active={tab === t.key}
                onClick={() => setTab(t.key)}
              >
                <span className="mr-1">{t.icon}</span>
                {t.label}
              </Pill>
            ))}
          </div>
        </div>

        {/* Error */}
        {err && (
          <SectionCard>
            <p className="text-[14px] text-red-300">{err}</p>
          </SectionCard>
        )}

        {/* เนื้อหาตามแท็บ */}
        <SectionCard>
          <h3
            className="text-[18px] sm:text-[20px] font-extrabold text-left"
            style={{ color: GOLD }}
          >
            {content.title}
          </h3>
          <p
            className="mt-3 text-[13px] sm:text-[14px] leading-7 text-left"
            style={{ color: GOLD }}
          >
            {content.body || "—"}
          </p>
        </SectionCard>

        <SectionCard>
          <h4
            className="text-[16px] sm:text-[18px] font-extrabold text-left"
            style={{ color: GOLD }}
          >
            {content.tipTitle}
          </h4>
          <p
            className="mt-2 text-[13px] sm:text-[14px] text-left"
            style={{ color: GOLD }}
          >
            {content.tip || "—"}
          </p>
        </SectionCard>

        {/* เลขนำโชค */}
        {luckyNumbers.length > 0 && (
          <SectionCard>
            <h4
              className="text-[16px] sm:text-[18px] font-extrabold text-left"
              style={{ color: GOLD }}
            >
              เลขนำโชค
            </h4>
            <div className="mt-3 flex flex-wrap gap-2">
              {luckyNumbers.map((n, i) => (
                <span
                  key={`${n}-${i}`}
                  className="inline-flex items-center justify-center rounded-full px-3 py-1 text-[13px] font-semibold"
                  style={{ border: `1.25px solid ${GOLD}`, color: GOLD }}
                >
                  {n}
                </span>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Disclaimer */}
        <p className="text-[12px] text-white/55 text-center mt-6">
          ผลการวิเคราะห์จัดทำตามหลักโหราศาสตร์ไทย
        </p>
      </main>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100]">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 text-purple-700 font-semibold animate-pulse">
            <span className="text-2xl">🔮</span>
            <span>ระบบกำลังทำนายดวง โปรดรอสักครู่...</span>
          </div>
        </div>
      )}
    </div>
  );
}
